import { ScrollArea, Stack } from '@mantine/core';
import { useMemo, useState } from 'react';
import {
  ProfessionalAvailability,
} from '../../types/AvailabilityModels';
import { NoAvailabilitySet } from './components/appointments/NoAvailabilitySet';
import {
  TimeSelector,
  TopicSelection,
} from './components/appointments';
import { BookAppointmentForm } from './components/appointments/BookAppointmentForm';
import { useApp } from '../../context/AppContext';
import { NoEmailRender } from './components/appointments/NoEmailRender';
import { useQuery } from '@tanstack/react-query';
import { getBookings } from '../../services/BookingService';
import { getProfessionalProfile } from '../../services/ProfileService';

interface BookAppointmentsProps {
  professionalAvailability?: ProfessionalAvailability;
  professionalId: string;
}

export const BookAppointments = ({
  professionalAvailability,
  professionalId
}: BookAppointmentsProps) => {
  const { profile, token, scrollAreaHeight } = useApp();

  if (!profile?.hasEmail) {
    return <NoEmailRender />;
  }

  const hasFutureAvailability = useMemo(() => {
    if (!professionalAvailability?.availableDates?.length) return false;
    const now = new Date();
    return professionalAvailability.availableDates.some((date) =>
      date.availableTimes.some((time) => new Date(time.startIso) > now)
    );
  }, [professionalAvailability]);

  if (!hasFutureAvailability) {
    return <NoAvailabilitySet professionalId={professionalId} />;
  }

  const [selectedTopicSelections, setSelectedTopicSelections] = useState<
    TopicSelection[]
  >([]);

  const { data: bookings } = useQuery({
    queryKey: ['bookings-for-availability-filter'],
    enabled: !!token,
    queryFn: async () => {
      const bookingResponse = await getBookings(token);
      return bookingResponse?.bookings ?? [];
    },
  });

  const { data: professionalProfile } = useQuery({
    queryKey: ['professionalProfile', professionalId],
    enabled: !!professionalId,
    queryFn: async () => {
      return getProfessionalProfile(professionalId);
    },
  });

  const availableTimes = useMemo(() => {
    const now = new Date();
    return (professionalAvailability?.availableDates ?? []).flatMap((avDate) =>
      avDate.availableTimes.filter((time) => new Date(time.startIso) > now)
    );
  }, [professionalAvailability]);

  return (
    <ScrollArea
      h={Number(scrollAreaHeight.replace('px', '')) || undefined}
      type="auto"
      offsetScrollbars
    >
      <Stack gap={10} pb={20}>
      <TimeSelector
        availableTimes={availableTimes}
        allTopics={professionalProfile?.activeTopics ?? []}
        existingBookings={bookings ?? []}
        selectedTopicSelections={selectedTopicSelections}
        onApplyTopicSelection={(selection: TopicSelection) => {
          setSelectedTopicSelections((previous) => {
            const next = previous.filter(
              (existing) => existing.topicKey !== selection.topicKey
            );

            if (selection.times.length === 0) {
              return next;
            }

            return [...next, selection];
          });
        }}
      />
      <BookAppointmentForm
        professionalId={professionalId}
        selectedTopicSelections={selectedTopicSelections}
        onClearSelection={() => {
          setSelectedTopicSelections([]);
        }}
        professionalTimeZone={
          professionalAvailability?.availableDates?.[0]?.timeZoneId
        }
      />
      </Stack>
    </ScrollArea>
  );
};
